import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AccountNavigationExtras,
  AuthenticationContext,
  GotoRegistrationComplete,
  GotoSignIn,
  GotoSignInNavigationExtras,
  SessionState,
  SignInMode
} from '@platform-frontend/account';
import {
  GotoAcademicRegistration,
  GotoCommercialCheckout,
  GotoPurchaseInformation,
  GotoSubscriptionManagement,
  MetadataWithId,
  RefreshCheckoutMessageParams,
  RefreshSignInMessageParams,
  SetStartupTempData,
  StartupConfigurationState
} from '@platform-frontend/checkout';
import { Metadata } from '@platform-frontend/checkout-drop-in';
import { InvalidStateError, SimpleRouterNavigationExtras, WINDOW } from '@platform-frontend/util';
import { map, Observable, switchMap, throwError } from 'rxjs';
import { StartAcademicRegistrationMessageParams, StartCheckoutMessageParams, StartSignInMessageParams } from './model/web-checkout-sanitized-start-params';

@Injectable({
  providedIn: 'root'
})
export class WebCheckoutStartupRedirectionService {
  constructor(private store: Store, @Inject(WINDOW) private window: Window) {}

  signIn(
    navigate: (navigationAction: unknown) => Observable<unknown>,
    params: StartSignInMessageParams,
    extras?: SimpleRouterNavigationExtras
  ): Observable<unknown> {
    return this.setMetadataAndForceSignIn(params.metadata, params.metadataId, params.forceSignIn).pipe(
      switchMap(metadataId => this.gotoSignInOrRegistrationComplete(navAction => navigate(navAction), 'AUTO', { ...params, metadataId }, extras))
    );
  }

  gotoSignInOrRegistrationComplete(
    navigate: (navigationAction: unknown) => Observable<unknown>,
    signInMode: SignInMode,
    params: StartSignInMessageParams,
    extras?: SimpleRouterNavigationExtras
  ): Observable<unknown> {
    const authParams = this.buildAuthNavigationExtras(params, extras);
    if (this.store.selectSnapshot(SessionState.isSignedIn)) {
      const context: AuthenticationContext = 'SIGN_IN';
      return navigate(
        new GotoRegistrationComplete<AccountNavigationExtras<RefreshSignInMessageParams>>({
          ...authParams,
          queryParams: { context }
        })
      );
    }
    return navigate(new GotoSignIn({ ...authParams, signInMode }));
  }

  private buildAuthNavigationExtras(
    params: StartSignInMessageParams,
    extras?: SimpleRouterNavigationExtras
  ): GotoSignInNavigationExtras<RefreshSignInMessageParams> {
    // TODO (MP-2389): Remove redirect param, if possible. Currently, we must assure
    //                 that after sign-in PrepareFlow (i.e. FetchAssets) is dispatched.
    return {
      redirect: undefined,
      appStartParams: { vendorId: params.vendorId, locale: params.locale, metadataId: params.metadataId },
      ...extras
    };
  }

  startAcademicRegistration(
    navigate: (navigationAction: unknown) => Observable<unknown>,
    params: StartAcademicRegistrationMessageParams,
    extras?: SimpleRouterNavigationExtras
  ): Observable<unknown> {
    return this.setMetadataAndForceSignIn(params.metadata, params.metadataId, params.forceSignIn).pipe(
      switchMap(metadataId =>
        navigate(new GotoAcademicRegistration({ productIdentifier: params.productIdentifier, start: params.start, metadataId, locale: params.locale }, extras))
      )
    );
  }

  startCheckout(
    navigate: (navigationAction: unknown) => Observable<unknown>,
    params: StartCheckoutMessageParams,
    extras?: SimpleRouterNavigationExtras
  ): Observable<unknown> {
    return this.setMetadataAndForceSignIn(params.metadata, params.metadataId, params.forceSignIn).pipe(
      switchMap(metadataId => this.checkoutNavigate(navigate, { ...params, metadataId }, extras))
    );
  }

  private setMetadataAndForceSignIn(paramsMetadata: Metadata | null, paramsMetadataId: string | null, paramsForceSignIn: boolean): Observable<string | null> {
    let metadataId: string | null;
    try {
      metadataId = paramsMetadataId ?? (paramsMetadata ? this.window.crypto.randomUUID() : null);
    } catch (e) {
      const errorMsg = 'Failed to generate metadataId!' + this.window.isSecureContext ? '' : ' The checkout window is not running in a secure context!';
      return throwError(() => new InvalidStateError(errorMsg));
    }

    const metadataWithId: MetadataWithId | null = metadataId ? { id: metadataId, metadata: paramsMetadataId ? null : paramsMetadata } : null;
    return this.store.dispatch(new SetStartupTempData(metadataWithId, paramsForceSignIn)).pipe(map(() => metadataId));
  }

  private checkoutNavigate(
    navigate: (navigationAction: unknown) => Observable<unknown>,
    params: StartCheckoutMessageParams,
    extras?: SimpleRouterNavigationExtras
  ): Observable<unknown> {
    const refreshParams: RefreshCheckoutMessageParams = {
      productIdentifier: params.productIdentifier,
      contractId: params.contractId,
      start: params.start,
      metadataId: params.metadataId,
      locale: params.locale
    };
    if (params.manageAssetHint) {
      if (params.start?.kind?.type === 'SUBSCRIPTION') {
        return navigate(new GotoSubscriptionManagement(refreshParams, extras));
      }
      return navigate(new GotoPurchaseInformation(refreshParams, extras));
    }
    return navigate(new GotoCommercialCheckout(refreshParams, extras));
  }

  getCloseWindowRedirectUrl(closedFromConfirmationPage: boolean): string | null {
    return (
      (closedFromConfirmationPage ? this.store.selectSnapshot(StartupConfigurationState.onSuccessRedirectUrl) : null) ??
      this.store.selectSnapshot(StartupConfigurationState.onCloseRedirectUrl)
    );
  }
}
